import { LoaderFunctionArgs, useLoaderData } from "react-router"
import { H1, H2, P, Section } from "~/components/Typography"
import { DocsRouterLink as RouterLink, DocsUiLink as Link } from "~/docs-data/docs-link"
import { getCountryCatalog, methodologyForCountry, signalsForCountry } from "~/docs-data/helpers"
import { supplySources } from "~/docs-data/concepts"
import { getDocsCatalogForRequest } from "~/server/docs-catalog-store.server"

export const loader = async ({ request, params }: LoaderFunctionArgs) => {
  const { countries } = await getDocsCatalogForRequest(request)
  const cc = getCountryCatalog(countries, params.country ?? "")
  if (!cc) throw new Response("Country catalog not found", { status: 404 })
  return {
    cc,
    entries: methodologyForCountry(cc.countryCode),
    signals: signalsForCountry(cc.countryCode),
    supply: supplySources.filter((s) => s.country === cc.countryCode),
  }
}

export default function CountryMethodologyIndex() {
  const { cc, entries, signals, supply } = useLoaderData<typeof loader>()

  return (
    <div className="max-w-screen-xl">
      <Section>
        <H1>Methodology</H1>
        <P>
          How {cc.name} data is produced: how visits are detected, how observations are scaled to
          population, and how privacy is enforced. Methodology follows the underlying signal, so
          what applies in {cc.name} is determined by its supply.
        </P>
        {supply.length > 0 && (
          <P className="text-sm text-muted-foreground">
            {cc.name} is served by{" "}
            {supply.map((s, i) => (
              <span key={s.id}>
                {i > 0 && (i === supply.length - 1 ? " and " : ", ")}
                <Link to={`/docs/datasets/${cc.code}/supply/${s.id}`} variant="inline">
                  {s.shortName}
                </Link>
              </span>
            ))}
            . Methodology for other signals does not apply here and is not shown.
          </P>
        )}
      </Section>

      <Section>
        <H2>Foundations</H2>
        {entries.length === 0 ? (
          <div className="border rounded-md py-12 text-center text-muted-foreground">
            {signals.length === 0
              ? `No signal sources are recorded for ${cc.name} yet, so there is no methodology to show.`
              : `Methodology for ${cc.name} has not been documented yet.`}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {entries.map((m) => (
              <RouterLink
                key={m.id}
                to={`/docs/datasets/${cc.code}/methodology/${m.id}`}
                className="group block rounded-lg border bg-card hover:border-primary hover:shadow-sm transition-all p-5"
              >
                <h3 className="font-semibold text-base group-hover:text-primary transition-colors mb-1.5">
                  {m.title}
                </h3>
                <p className="text-sm text-muted-foreground line-clamp-3">{m.shortDescription}</p>
              </RouterLink>
            ))}
          </div>
        )}
      </Section>

      <Section>
        <H2>Per-model methodology</H2>
        <P className="text-sm text-muted-foreground">
          The pages above cover the foundations shared across {cc.name}.
          Model-specific facts, such as production lag and data flags, are
          shown on each dataset&apos;s page.
        </P>
      </Section>
    </div>
  )
}
